import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformNotificationTemplateDetailPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/notification-templates/${templateId}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setTemplate(json.template);
    } catch (e) {
      setError(e.message);
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (ready) load();
  }, [load, ready]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await platformApiFetch(`/api/platform/notification-templates/${templateId}`, { method: 'DELETE' });
      if (res.status === 204 || res.ok) {
        navigate('/platform/notification-templates');
        return;
      }
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Delete failed');
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
      setDelOpen(false);
    }
  };

  if (!ready || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !template) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!template) return null;

  let varsPretty = '—';
  if (template.variables_json != null) {
    try {
      varsPretty = JSON.stringify(template.variables_json, null, 2);
    } catch (_e) {
      varsPretty = String(template.variables_json);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5">{template.template_name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
            Global · {template.template_code}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={template.channel} />
            <Chip size="small" label={template.status} color={statusColor(template.status)} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" component={Link} to="/platform/notification-templates">
            Back to list
          </Button>
          {isSuper && (
            <>
              <Button variant="contained" component={Link} to={`/platform/notification-templates/${templateId}/edit`}>
                Edit
              </Button>
              <Button color="error" variant="outlined" onClick={() => setDelOpen(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!isSuper && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only for your role. Super admins can edit or delete global templates.
        </Alert>
      )}

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Preview
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {template.subject != null && String(template.subject).trim() !== '' && (
          <Typography variant="subtitle1" gutterBottom>
            Subject: {template.subject}
          </Typography>
        )}
        <Typography component="div" variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
          {template.body}
        </Typography>
      </Paper>

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Variables (JSON)
      </Typography>
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {varsPretty}
        </Typography>
      </Paper>

      <Dialog open={delOpen} onClose={() => !deleting && setDelOpen(false)}>
        <DialogTitle>Delete global template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {template.template_name} ({template.template_code})? Recorded in platform audit log.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={doDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
